import { Component, OnInit, OnDestroy, inject, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';


import { AuthFlowService } from '@core/services/auth-flow.service';
import { AuthService } from '@core/services/auth.service';
import { UserService } from '@core/services/user.service';
import { CompanyService } from '@core/services/company.service';
import { ThemeService } from '@core/services/theme.service';
import { ProfileValidationService } from '@core/services/profile-validation.service';
import { TierAccessService } from '@core/services/tier-access.service';

import { User } from '@core/models/user';
import { Company } from '@core/models/company';

/**
 * SuccessComponent
 *
 * Displays registration completion confirmation with eval account details,
 * onboarding instructions, and next steps for users after completing
 * the freemium registration flow.
 */
@Component({
  selector: 'app-success',
  templateUrl: './success.component.html',
  styleUrl: './success.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [],
})
export class SuccessComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private authFlowService = inject(AuthFlowService);
  private userService = inject(UserService);
  private companyService = inject(CompanyService);
  private themeService = inject(ThemeService);
  private profileValidationService = inject(ProfileValidationService);
  private tierAccessService = inject(TierAccessService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  user?: User;
  company?: Company;
  loading = true;
  error = false;

  // Success page data
  accountType = '';
  welcomeMessage = '';
  nextSteps: string[] = [];
  accountFeatures: string[] = [];
  supportInfo = {
    email: 'support@scanninja.ai',
    documentation: 'https://docs.scanninja.ai',
    communityForum: 'https://community.scan-ninja.com',
  };

  // Timer for auto-redirect
  redirectTimer = 10;
  private redirectInterval?: number;

  constructor() {
    this.themeService.setSelectPlanPageLayout(true);
  }

  ngOnInit(): void {
    this.loadUserData();
    this.startRedirectTimer();
  }

  ngOnDestroy(): void {
    this.themeService.setSelectPlanPageLayout(false);
    if (this.redirectInterval) {
      clearInterval(this.redirectInterval);
    }
  }

  /**
   * Load user and company data to display success information
   */
  private async loadUserData(): Promise<void> {
    try {
      const user = await this.authService.waitForHydratedUser(10000);
      if (!user) {
        this.error = true;
        this.loading = false;
        return;
      }

      this.user = user;

      if (user.currentCompanyId) {
        const company = await this.companyService.getCompany(user.currentCompanyId);
        if (company) {
          this.company = company;
        }
      }

      this.setupSuccessPageData();
      this.loading = false;
    } catch (error) {
      console.error('Error loading user data:', error);
      this.error = true;
      this.loading = false;
    }
  }

  /**
   * Setup success page data based on user tier and registration completion
   */
  private setupSuccessPageData(): void {
    if (!this.user || !this.company) return;

    const tier = this.company.tier || 'free';
    const companyName = this.company.name || 'your organization';

    // Setup account type and welcome message
    switch (tier) {
      case 'free':
        // Every new registration lands here: the account exists, and scanning
        // starts once a plan is paid for at checkout (#847).
        this.accountType = 'Account Created';
        this.welcomeMessage = `Welcome to Scan Ninja! Your account has been created for ${companyName}. Choose a plan to start scanning.`;
        this.nextSteps = [
          'Choose a plan and complete checkout',
          'Set up your first vulnerability scan',
          'Check out our getting started guide',
          'Talk to our sales team about Enterprise or a guided trial',
        ];
        this.accountFeatures = [
          'Plans include vulnerability scanning, compliance and AI analysis',
          'Monthly or annual billing',
        ];
        break;

      case 'trial':
        this.accountType = 'Trial Account';
        this.welcomeMessage = `Your trial is active for ${companyName}.`;
        this.nextSteps = [
          'Set up your first vulnerability scan',
          'Configure your scanning targets',
          'Review your technical preferences',
          'Connect with our onboarding team',
          'Explore advanced reporting features',
        ];
        this.accountFeatures = [
          'Full vulnerability scanning',
          'Complete dashboard access',
          'Unlimited AI queries',
          'Advanced reporting',
          'Email and phone support',
          'Custom integrations',
        ];
        break;

      case 'premium':
        this.accountType = 'Premium Account';
        this.welcomeMessage = `Welcome to Scan Ninja Premium! Your account has been set up for ${companyName}.`;
        this.nextSteps = [
          'Schedule onboarding call with dedicated support agent',
          'Set up enterprise-grade scanning infrastructure',
          'Configure advanced compliance reporting',
          'Explore MSSP management tools',
          'Access premium training resources',
        ];
        this.accountFeatures = [
          'Enterprise vulnerability scanning',
          'Advanced analytics and reporting',
          'Compliance verification tools',
          'Dedicated support agent',
          'Custom integrations and API access',
          'MSSP management features',
          'Priority security updates',
        ];
        break;
    }
  }

  /**
   * Start countdown timer for auto-redirect
   */
  private startRedirectTimer(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.redirectInterval = window.setInterval(() => {
      this.redirectTimer--;
      if (this.redirectTimer <= 0) {
        this.proceedToDashboard();
      }
    }, 1000);
  }

  /** True while the company has no plan: the next step is choosing one. */
  get needsPlan(): boolean {
    const tier = this.company?.tier;
    return !tier || tier === 'free';
  }

  /**
   * Proceed through the post-login flow, which sends a company with no plan
   * to plan selection and everyone else to the dashboard.
   */
  proceedToDashboard(): void {
    if (this.redirectInterval) {
      clearInterval(this.redirectInterval);
    }
    void this.router.navigateByUrl('/logging-in', { replaceUrl: true });
  }

  /**
   * Contact support
   */
  contactSupport(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.open(
      `mailto:${this.supportInfo.email}?subject=New Account Setup - ${this.company?.name}`,
      '_blank',
    );
  }

  /**
   * Open documentation
   */
  openDocumentation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.open(this.supportInfo.documentation, '_blank');
  }

  /**
   * Open community forum
   */
  openCommunity(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.open(this.supportInfo.communityForum, '_blank');
  }

  /**
   * Get tier badge class
   */
  getTierBadgeClass(): string {
    switch (this.company?.tier) {
      case 'trial':
        return 'bg-warning';
      case 'premium':
        return 'bg-primary';
      default:
        return 'bg-success';
    }
  }

  /**
   * Get tier icon
   */
  getTierIcon(): string {
    switch (this.company?.tier) {
      case 'trial':
        return 'bi-clock-fill';
      case 'premium':
        return 'bi-star-fill';
      default:
        return 'bi-check-circle-fill';
    }
  }

  /**
   * Get trial days remaining (if applicable)
   */
  getTrialDaysRemaining(): number {
    if (!this.company?.trialEndDate) return 0;

    const endDate = new Date(this.company.trialEndDate);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }

  /**
   * Check if trial is active
   */
  isTrialActive(): boolean {
    return this.company?.tier === 'trial' && this.getTrialDaysRemaining() > 0;
  }
}
